import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Typography, TextField, Grid, Switch, FormControlLabel,
    LinearProgress, Breadcrumbs, Link, Stack
    } from '@mui/material';
import { Home } from '@mui/icons-material';
import categoriesApi from '../../../../services/categories';
import AnimateButton from '../../../AnimateButton';
import { BUTTON_CANCEL_COLOR, BUTTON_SAVE_COLOR } from '../../../common/color';
import Swal from 'sweetalert2';

const CategoriesBody = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const isNew = id === 'new';
    const [formData, setFormData] = useState({
        catName: '',
        description: '',
        isActive: true
    });
    const [previewUrl] = useState('');
    const [errors, setErrors] = useState({ catName: '', description: '' });
    const [touched, setTouched] = useState({ catName: false, description: false});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isNew) {
            const fetchById = async () => {
                try {
                    const category = await categoriesApi.getById(id);
                    setFormData({
                        catName: category.catName || '',
                        description: category.description || '',
                        isActive: category.isActive
                    });

                } catch (error) {
                    console.error('Fetch error:', error);
                    Swal.fire('Error', 'Failed to load category', 'error');
                    navigate('/admin/categories');
                } finally {
                    setLoading(false);
                }
            };
            fetchById();
        } else {
            setLoading(false);
        }
    }, [id, isNew, navigate]);

    useEffect(() => {
        return () => {
            if (previewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const validateField = (name, value) => {
        switch (name) {
            case 'catName': return value.trim() ? '' : 'Name is required';
            case 'description': return value.trim() ? '' : 'Description is required';
            default: return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (touched[name]) {
            setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        setErrors(prev => ({ ...prev, [name]: validateField(name, formData[name]) }));
    };



    const handleSubmit = async () => {
        setTouched({ catName: true, description: true });
        if (!formData.catName.trim() || !formData.description.trim()) {
            setErrors({
                catName: !formData.catName.trim() ? 'Name is required' : '',
                description: !formData.description.trim() ? 'Description is required' : ''
            });
            return;
        }


        const confirm = await Swal.fire({
            title: 'Confirm',
            text: `Are you sure you want to ${isNew ? 'create' : 'update'} this category?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: BUTTON_SAVE_COLOR,
            cancelButtonColor: BUTTON_CANCEL_COLOR
        });

        if (!confirm.isConfirmed) return;

        setIsSubmitting(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('catName', formData.catName);
            formDataToSend.append('description', formData.description || '');
            formDataToSend.append('isActive', formData.isActive);


            if (isNew) {
                await categoriesApi.create(formDataToSend);
            } else {
                await categoriesApi.update(id, formDataToSend);
            }

            Swal.fire('Success', `Category ${isNew ? 'created' : 'updated'}`, 'success');
            navigate('/admin/categories');
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.response?.data?.error || 'Operation failed', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <LinearProgress />;

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Dialog open fullWidth maxWidth="md" onClose={() => navigate('/admin/categories')}>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between">
                        <Breadcrumbs>
                            <Link href="/admin" underline="hover" color="inherit">
                                <Home sx={{ mr: 0.5 }} /> Dashboard
                            </Link>
                            <Link href="/admin/categories" underline="hover" color="inherit">
                                Categories
                            </Link>
                            <Typography color="text.primary">
                                {isNew ? 'Create' : 'Edit'}
                            </Typography>
                        </Breadcrumbs>
                    </Stack>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" gutterBottom>Category Information</Typography>

                            <TextField
                                required
                                fullWidth
                                label="Name"
                                name="catName"
                                value={formData.catName}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.catName && Boolean(errors.catName)}
                                helperText={touched.catName && errors.catName}
                                sx={{ mb: 3 }}
                                size="small"
                            />

                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                sx={{ mb: 3 }}
                                size="small"
                            />


                            <FormControlLabel
                                control={<Switch checked={formData.isActive} onChange={(e) =>
                                    setFormData(prev => ({ ...prev, isActive: e.target.checked }))
                                } />}
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <Divider />

                <DialogActions sx={{ p: 3 }}>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/admin/categories')}
                            sx={{ background: BUTTON_CANCEL_COLOR }}
                        >
                            Cancel
                        </Button>
                    </AnimateButton>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{ background: BUTTON_SAVE_COLOR }}
                        >
                            {isSubmitting ? 'Saving...' : isNew ? 'Create' : 'Save'}
                        </Button>
                    </AnimateButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CategoriesBody;